import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { MarkdownReact } from "@/components/MarkdownReact";
import { ONBOARDING_TASK_TYPE, Permission } from "@forgepoint/shared";
import { useHasPermission } from "@/store/authStore";
import { useOnboardingStore } from "./onboardingStore";

interface OnboardingTaskSheetProps {
  taskId: string | null;
  onClose: () => void;
}

/**
 * Slide-over showing an onboarding task: the name as title, the markdown
 * description below, and a mark-as-complete toggle in the header. Opened from
 * the onboarding page task rows and the inbox onboarding section.
 */
export function OnboardingTaskSheet({
  taskId,
  onClose,
}: OnboardingTaskSheetProps) {
  const canInteract = useHasPermission(Permission.ONBOARDING_INTERACT);
  const task = useOnboardingStore((state) =>
    taskId ? (state.taskMap.get(taskId) ?? null) : null,
  );
  const isToggling = useOnboardingStore((state) =>
    taskId ? (state.taskStateById.get(taskId)?.isToggling ?? false) : false,
  );
  const toggleTask = useOnboardingStore((state) => state.toggleTask);

  return (
    <Sheet open={task !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full flex-col gap-4 overflow-hidden sm:w-[560px] sm:max-w-[560px]">
        {task && (
          <>
            <SheetHeader>
              <div className="flex items-start justify-between gap-3">
                <SheetTitle className="flex flex-wrap items-center gap-2">
                  {task.name}
                  {task.type === ONBOARDING_TASK_TYPE.UAT && (
                    <Badge variant="outline">UAT</Badge>
                  )}
                </SheetTitle>
                <Button
                  size="sm"
                  variant={task.completed ? "outline" : "default"}
                  className={
                    task.completed
                      ? "shrink-0"
                      : "shrink-0 bg-green-600 text-white hover:bg-green-700"
                  }
                  disabled={!canInteract || isToggling}
                  onClick={() => toggleTask(task.id, !task.completed)}
                >
                  {task.completed ? "Mark as incomplete" : "Mark as complete"}
                  {isToggling && <Loader2 className="h-4 w-4 animate-spin" />}
                </Button>
              </div>
            </SheetHeader>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {task.description ? (
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <MarkdownReact>{task.description}</MarkdownReact>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No description.</p>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
